import { createBlockStash } from './codeBlocks.js';

// Progressive formatting for streaming chunks
export function formatStreamingChunk(text) {
  if (!text) return '';

  // Remove Grok/Perplexity meta tags before processing
  text = text.replace(/<grok:render[^>]*>[\s\S]*?<\/grok:render>/g, '');
  text = text.replace(/<argument[^>]*>[\s\S]*?<\/argument>/g, '');

  // Preserve network-slice-wiz web component before HTML escaping
  const webComponents = [];
  text = text.replace(/(<network-slice-wiz[^>]*>[\s\S]*?<\/network-slice-wiz>)/g, (match) => {
    const placeholder = `__WEB_COMPONENT_${webComponents.length}__`;
    webComponents.push(match);
    return placeholder;
  });

  // Escape HTML first to prevent XSS
  let content = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Complete code blocks are rendered up front (arrow chains become flow
  // diagrams) and parked behind a token, so the rules below leave them alone.
  // Incomplete fences mid-stream simply don't match and stay as text.
  const blockStash = createBlockStash();
  content = blockStash.stash(content);

  // Detect if we have a table pattern (even incomplete)
  const hasTablePattern = /\|.*\|/.test(content);

  if (hasTablePattern) {
    // Try to format complete tables with separator lines
    const originalContent = content;
    content = content.replace(
      /^\s*(\|.+\|)\s*\n\s*(\|[-:\s|]+\|)\s*\n((?:\s*\|.+\|(?:\s*\n|$))+)/gm,
      function (match, headerLine, separatorLine, rows) {
        let html = '<table class="streaming-table"><thead><tr>';
        const header = headerLine.replace(/^\s*\||\|\s*$/g, '').trim();
        header
          .split('|')
          .forEach(cell => {
            const trimmedCell = cell.trim();
            if (trimmedCell) {
              html += `<th>${trimmedCell}</th>`;
            }
          });
        html += '</tr></thead><tbody>';
        rows
          .trim()
          .split('\n')
          .forEach(row => {
            if (!row.trim() || !row.includes('|')) return;
            const cleanRow = row.replace(/^\s*\||\|\s*$/g, '').trim();
            if (!cleanRow) return;

            html += '<tr>';
            cleanRow.split('|').forEach(cell => {
              const trimmedCell = cell.trim();
              if (trimmedCell) {
                html += `<td>${trimmedCell}</td>`;
              }
            });
            html += '</tr>';
          });
        html += '</tbody></table>';
        return html + '\n'; // Preserve line break for subsequent content
      }
    );

    // Group consecutive pipe-delimited rows into tables (without separator lines)
    content = content.replace(
      /((?:^\s*\|.+\|\s*$\n?)+)/gm,
      function(match) {
        // Skip if already processed as table
        if (match.includes('<table') || match.includes('<th>')) return match;

        const lines = match.trim().split('\n').filter(line => line.trim());
        if (lines.length < 2) return match; // Need at least 2 rows for a table

        // First row is header
        const headerCells = lines[0].replace(/^\s*\||\|\s*$/g, '').split('|').map(c => c.trim()).filter(c => c);

        let html = '<table class="streaming-table"><thead><tr>';
        headerCells.forEach(cell => {
          html += `<th>${cell}</th>`;
        });
        html += '</tr></thead><tbody>';

        // Rest are data rows
        for (let i = 1; i < lines.length; i++) {
          const cells = lines[i].replace(/^\s*\||\|\s*$/g, '').split('|').map(c => c.trim()).filter(c => c);
          html += '<tr>';
          cells.forEach(cell => {
            html += `<td>${cell}</td>`;
          });
          html += '</tr>';
        }

        html += '</tbody></table>\n';
        return html;
      }
    );

    // Only wrap in streaming-content if no tables were successfully formatted
    // and the content still has unformatted table patterns
    if (content === originalContent && /\|.*\|.*\n.*\|.*\|/.test(content)) {
      // Only wrap the table-like parts, not the entire content
      content = content.replace(
        /(\|[^|\n]*\|(?:\s*\n\s*\|[^|\n]*\|)*)/g,
        '<div class="streaming-content">$1</div>'
      );
    }
  }

  // Convert structured bullet lists to tables (e.g., API responses with key-value pairs)
  // Pattern: - **Key** – Description (with various separators: en-dash, em-dash, colon, hyphen)
  // Only applies to TOP-LEVEL bullets (no indentation) to avoid conflicts with nested lists
  content = content.replace(
    /(?:^- \*\*[^*]+\*\*\s*[–—:\-]\s*.+$(?:\n|$))+/gm,
    function(match, offset, fullString) {
      const lines = match.trim().split('\n').filter(line => line.trim());

      // Need at least 2 lines to make a table worthwhile
      if (lines.length < 2) return match;

      // Check if this block is followed by indented bullets (nested content)
      // If so, don't convert to table - it's a nested list structure
      const afterMatch = fullString.substring(offset + match.length);
      const nextLine = afterMatch.match(/^[ \t]*[^\n]*/);
      if (nextLine && /^  +[-*+]/.test(nextLine[0])) {
        // Next line is an indented bullet - this is a nested list, not a table
        return match;
      }

      // Check if all lines follow the pattern AND have substantial content after separator
      const validLines = lines.filter(line => {
        const match = line.match(/^- \*\*[^*]+\*\*\s*[–—:\-]\s*(.+)$/);
        if (!match) return false;
        // Require substantial content (not just whitespace or very short text)
        const content = match[1].trim();
        return content.length > 3;
      });

      if (validLines.length < 2) return match;

      let html = '<table class="streaming-table"><thead><tr><th>Item</th><th>Description</th></tr></thead><tbody>';

      validLines.forEach(line => {
        const itemMatch = line.match(/^- \*\*([^*]+)\*\*\s*[–—:\-]\s*(.+)$/);
        if (itemMatch) {
          const key = itemMatch[1].trim();
          const description = itemMatch[2].trim();
          html += `<tr><td><strong>${key}</strong></td><td>${description}</td></tr>`;
        }
      });

      html += '</tbody></table>\n';
      return html;
    }
  );

  // Apply basic formatting that works well for streaming
  // Headers (allow optional leading whitespace for consistency with final formatter)
  content = content.replace(/^\s*###### (.*)$/gm, '<h6>$1</h6>');
  content = content.replace(/^\s*##### (.*)$/gm, '<h5>$1</h5>');
  content = content.replace(/^\s*#### (.*)$/gm, '<h4>$1</h4>');
  content = content.replace(/^\s*### (.*)$/gm, '<h3>$1</h3>');
  content = content.replace(/^\s*## (.*)$/gm, '<h2>$1</h2>');
  content = content.replace(/^\s*# (.*)$/gm, '<h1>$1</h1>');

  // Bold and italic (only complete patterns to avoid breaking mid-stream)
  content = content.replace(/\*\*([^*\n]{2,}?)\*\*/g, '<strong>$1</strong>');
  content = content.replace(/(?<!\*)\*([^*\n]{2,}?)\*(?!\*)/g, '<em>$1</em>');
  content = content.replace(/__([^_\n]{2,}?)__/g, '<strong>$1</strong>');
  content = content.replace(/(?<!_)_([^_\n]{2,}?)_(?!_)/g, '<em>$1</em>');

  // Inline code (only complete backticks)
  content = content.replace(/`([^`\n]+)`/g, '<code>$1</code>');

  // Lists - handle multi-level nested lists by preserving indentation
  // Mark bullets with their indentation level (0, 2, 4, 6 spaces, etc.)
  // Supports -, *, +, and • (Unicode bullet U+2022)
  content = content.replace(/^([ ]*)[\*\-\+•] (.+)$/gm, function(match, spaces, text) {
    const level = spaces.length / 2; // 0 spaces = level 0, 2 spaces = level 1, 4 = level 2, etc.
    // Trim trailing spaces to prevent unwanted <br> tags from markdown line breaks
    return `<li-ul-L${level}>${text.trim()}</li-ul-L${level}>`;
  });

  // Process from deepest nesting to shallowest to build proper hierarchy
  // Level 3 (6 spaces)
  content = content.replace(/((?:<li-ul-L3>.*?<\/li-ul-L3>\s*)+)/g, function(match) {
    return '<ul class="nested-list level-3">' + match.replace(/\s*<\/li-ul-L3>\s*<li-ul-L3>\s*/g, '</li><li>').replace(/<li-ul-L3>/g, '<li>').replace(/<\/li-ul-L3>/g, '</li>') + '</ul>';
  });
  // Move level-3 nested lists inside their parent level-2 items
  content = content.replace(/<\/li-ul-L2>\s*(<ul class="nested-list level-3">.*?<\/ul>)/gs, ' $1</li-ul-L2>');

  // Level 2 (4 spaces)
  content = content.replace(/((?:<li-ul-L2>(?:.*?(?:<ul class="nested-list level-3">.*?<\/ul>)?.*?)<\/li-ul-L2>\s*)+)/gs, function(match) {
    return '<ul class="nested-list level-2">' + match.replace(/\s*<\/li-ul-L2>\s*<li-ul-L2>\s*/g, '</li><li>').replace(/<li-ul-L2>/g, '<li>').replace(/<\/li-ul-L2>/g, '</li>') + '</ul>';
  });
  // Move level-2 nested lists inside their parent level-1 items
  content = content.replace(/<\/li-ul-L1>\s*(<ul class="nested-list level-2">.*?<\/ul>)/gs, ' $1</li-ul-L1>');

  // Level 1 (2 spaces)
  content = content.replace(/((?:<li-ul-L1>(?:.*?(?:<ul class="nested-list level-[23]">.*?<\/ul>)?.*?)<\/li-ul-L1>\s*)+)/gs, function(match) {
    return '<ul class="nested-list level-1">' + match.replace(/\s*<\/li-ul-L1>\s*<li-ul-L1>\s*/g, '</li><li>').replace(/<li-ul-L1>/g, '<li>').replace(/<\/li-ul-L1>/g, '</li>') + '</ul>';
  });
  // Move level-1 nested lists inside their parent level-0 items
  content = content.replace(/<\/li-ul-L0>\s*(<ul class="nested-list level-1">.*?<\/ul>)/gs, ' $1</li-ul-L0>');

  // Level 0 (top-level, no indentation)
  content = content.replace(/((?:<li-ul-L0>(?:.*?(?:<ul class="nested-list level-[123]">.*?<\/ul>)?.*?)<\/li-ul-L0>\s*)+)/gs, function(match) {
    return '<ul>' + match.replace(/\s*<\/li-ul-L0>\s*<li-ul-L0>\s*/g, '</li><li>').replace(/<li-ul-L0>/g, '<li>').replace(/<\/li-ul-L0>/g, '</li>') + '</ul>';
  });

  // Clean up any remaining tags
  for (let i = 0; i <= 3; i++) {
    content = content.replace(new RegExp(`<li-ul-L${i}>`, 'g'), '<li>');
    content = content.replace(new RegExp(`</li-ul-L${i}>`, 'g'), '</li>');
  }

  // Clean up excessive whitespace around lists to prevent unwanted line breaks
  // Remove multiple newlines after closing nested lists
  content = content.replace(/(<\/ul>)\s*\n\s*\n+\s*/g, '$1\n');
  // Normalize whitespace between closing and opening list tags
  content = content.replace(/(<\/li>)\s*\n\s*\n+\s*(<li>)/g, '$1\n$2');
  // Remove extra whitespace after list closings before next content
  content = content.replace(/(<\/ul>)\s+(<li>)/g, '$1 $2');

  // Numbered lists
  content = content.replace(/^(\d+)\. (.+)$/gm, '<div class="numbered-point">$1. $2</div>');

  // Line breaks and paragraphs
  content = content.replace(/\n\n+/g, '</p><p>');
  content = content.replace(/\n/g, '<br>');

  // Wrap in paragraph if not already wrapped and doesn't start with a header.
  // The test has to stay anchored — an unanchored `<div` matched anywhere in
  // the chunk, which left the opening <p> off and produced a stray </p>.
  if (!content.match(/^\s*<(?:h[1-6]|div|p|table|ul|ol|pre)\b/)) {
    content = '<p>' + content + '</p>';
  }

  // Clean up empty paragraphs and unnecessary blank lines
  content = content.replace(/<p>\s*<\/p>/g, '');
  content = content.replace(/<p>(<br\s*\/?>)+\s*<\/p>/gi, '');
  content = content.replace(/<p>\s+<\/p>/g, '');
  // Remove empty paragraphs between structural elements
  content = content.replace(/(<\/h[1-6]>)\s*<p>\s*<\/p>\s*/gi, '$1');
  content = content.replace(/(<\/h[1-6]>)\s*<p>(<br\s*\/?>)+<\/p>\s*/gi, '$1');
  content = content.replace(/(<\/[uo]l>)\s*<p>\s*<\/p>\s*/gi, '$1');
  // Remove breaks immediately after headers and lists
  content = content.replace(/(<\/h[1-6]>)\s*(<br\s*\/?>)+/gi, '$1');
  content = content.replace(/(<\/[uo]l>)\s*(<br\s*\/?>)+/gi, '$1');
  // Remove multiple consecutive breaks
  content = content.replace(/(<br\s*\/?>){2,}/gi, '<br>');
  // Final cleanup: remove any remaining empty paragraphs
  content = content.replace(/<p>\s*<\/p>/g, '');

  // Convert escaped <br> tags back to actual line breaks
  // This allows legitimate line breaks in content (especially in tables) to render properly
  content = content.replace(/&lt;br&gt;/gi, '<br>');
  content = content.replace(/&lt;br\s*\/&gt;/gi, '<br>');

  // Restore rendered code blocks / flow diagrams
  content = blockStash.restore(content);

  // Restore network-slice-wiz web components
  webComponents.forEach((component, index) => {
    content = content.replace(`__WEB_COMPONENT_${index}__`, component);
  });

  return content;
}
